/**
 * CollapsibleSection — a section that can be folded away WITHOUT hiding what it says.
 * ===========================================================================
 *
 * The Studio has grown long enough that finding a feature means scrolling past several tables that are
 * only occasionally relevant. The operator's framing is the one that matters: scrolling is *"not hideous
 * if something is actually down screen, but not great if you are hunting a feature."*
 *
 * ⭐ SO THE DESIGN RULE IS: A COLLAPSED SECTION MUST STILL ANSWER ITS OWN QUESTION. A header reading
 * "ACX, as achieved ▸" forces the operator to open it to learn anything, which makes collapsing a tax
 * rather than a saving — they open every section, and the scroll is back. A header reading
 * "ACX, as achieved · 8 checks · tightest 0.73 dB ▸" means folding it away costs nothing, because the
 * number that would have made them look is already on the outside.
 *
 * That is what `summary` is for, and why it is not optional-by-convention: pass the thing the section
 * exists to tell you.
 *
 * ⚠️ localStorage IS WRAPPED, EVERY TIME. It throws in a private window and with site data blocked, and
 * it can come back empty for reasons that have nothing to do with this app — so every read and write is
 * guarded and the component renders correctly without it. This mirrors `TREE_COLLAPSED_KEY` in
 * RenderingStudio, which is the pattern already in use here.
 */
import React, { useEffect, useState } from "react";
import { Header, Icon } from "semantic-ui-react";

/** Read the remembered state. `defaultOpen` wins when storage is empty or unavailable. */
const readOpen = (key, defaultOpen) => {
  if (!key) return defaultOpen;
  try {
    const v = localStorage.getItem(key);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch (_) { /* private window, blocked storage — fall through to the default */ }
  return defaultOpen;
};

const CollapsibleSection = ({
  /** Storage key, so a fold survives a reload. Omit it for a section that should always start open. */
  storageKey = null,
  title,
  /** One line under the title, shown only when OPEN — it explains, and explanations are for readers. */
  subtitle = null,
  /**
   * ⭐ The headline, shown when CLOSED. This is the whole point: put the number here that would
   * otherwise force someone to open the section.
   */
  summary = null,
  /** Shown in both states, before the caret — for a warning that must never be folded out of sight. */
  badge = null,
  defaultOpen = true,
  as = "h5",
  children,
  onToggle = null,
}) => {
  const [open, setOpen] = useState(() => readOpen(storageKey, defaultOpen));

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch (_) { /* storage unavailable */ }
  }, [storageKey, open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (onToggle) onToggle(next);
  };

  return (
    <div className="rs-collapsible">
      <Header
        as={as}
        className="rs-collapsible-header"
        onClick={toggle}
        // Keyboard-reachable: this is a control, and a div that only responds to a mouse is not one.
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
        }}
        aria-expanded={open}
        style={{ cursor: "pointer", marginBottom: open ? 4 : 0, userSelect: "none" }}
      >
        <Icon name={open ? "caret down" : "caret right"} />
        <Header.Content>
          {title}
          {badge != null && <span style={{ marginLeft: 8 }}>{badge}</span>}
          {/* Closed: the summary stands in for the content. Open: the subtitle explains it. */}
          {!open && summary != null && (
            <Header.Subheader style={{ marginTop: 2 }}>{summary}</Header.Subheader>
          )}
          {open && subtitle != null && (
            <Header.Subheader style={{ marginTop: 2 }}>{subtitle}</Header.Subheader>
          )}
        </Header.Content>
      </Header>
      {open && children}
    </div>
  );
};

export default CollapsibleSection;
